import * as React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useTranslations } from 'next-intl'

// import WithAnalytics from 'components/WithAnalytics'
// import MetrikaTracker from 'components/MetrikaTracker'
import SvgSprite from 'components/SvgSprite'
import DashboardFooter from 'components/DashboardFooter'

import AuthContext from 'lib/authContext'
import { globalStyle, colors } from 'utils/style'
// import { compose } from 'utils/misc'

import type { MessageKey } from '../types/i18n'
import { PROTECTED_ROUTES } from '../constants'

function getTitle(title: string | undefined, defaultTitle: string, tail: string) {
  if (title) {
    return `${title} — ${tail}`
  }

  return defaultTitle
}

type Props = {
  children: React.ReactNode
  title?: MessageKey
}

function DashboardLayout({ children, title: titleKey }: Props) {
  const authContext = React.useContext(AuthContext)
  const router = useRouter()
  const t = useTranslations()
  const metaTitle = t('meta.title')
  const metaDescription = t('meta.description')

  React.useEffect(() => {
    if (
      PROTECTED_ROUTES.includes(router.route) &&
      authContext.isRehydrated &&
      !authContext.token
    ) {
      router.push('/')
    }
  }, [router, authContext])

  return (
    <div className="root">
      <Head>
        <title>
          {getTitle(
            titleKey ? t(titleKey) : undefined,
            metaTitle,
            'Shipper',
          )}
        </title>

        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0"
        />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#455ac6" />
        <meta name="msapplication-TileColor" content="#ffffff" />
        <meta name="theme-color" content="#ffffff" />

        <meta
          name="title"
          content={metaTitle}
        />
        <meta
          name="description"
          content={metaDescription}
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://shipper.kz/" />
        <meta
          property="og:title"
          content={metaTitle}
        />
        <meta
          property="og:description"
          content={metaDescription}
        />
        <meta property="og:image" content="https://shipper.kz/shipper-og.jpg" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://shipper.kz/" />
        <meta
          property="twitter:title"
          content={metaTitle}
        />
        <meta
          property="twitter:description"
          content={metaDescription}
        />
        <meta
          property="twitter:image"
          content="https://shipper.kz/shipper-og.jpg"
        />
      </Head>

      <SvgSprite />

      <div className="main">{children}</div>

      <DashboardFooter />

      {/* <MetrikaTracker /> */}

      <style jsx global>
        {globalStyle}
      </style>
      <style jsx global>{`
        body {
          background-color: ${colors.faqBg};
        }

        h2 {
          color: ${colors.dashboardDarkBlue};
        }

        body.bx24-hidden [id*="b24"],
        body.bx24-hidden [class*="b24-widget"],
        body.bx24-hidden [class*="bx-livechat"] {
          display: none !important;
        }
      `}</style>
      <style jsx>{`
        .root {
          display: flex;
          position: relative;
          min-height: 100vh;
          flex-direction: column;
        }

        .main {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
      `}</style>
    </div>
  )
}

export default DashboardLayout
